import { useState, useCallback } from 'react';

/**
 * Risultato della validazione
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Funzione validatore
 */
export type Validator = (value: any) => ValidationResult;

/**
 * Schema di validazione per un form
 */
export type ValidationSchema = Record<string, Validator[]>;

/**
 * Errori di validazione per campo
 */
export type ValidationErrors = Record<string, string | undefined>;

/**
 * Campi toccati dall'utente
 */
export type TouchedFields = Record<string, boolean>;

/**
 * Valori del form
 */
export type FormValues = Record<string, any>;

// ============================================================================
// VALIDATORI BASE
// ============================================================================

/**
 * Validatore per campi obbligatori
 */
export function required(message: string = 'Campo obbligatorio'): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined) {
      return { valid: false, error: message };
    }

    if (typeof value === 'string' && value.trim() === '') {
      return { valid: false, error: message };
    }

    if (Array.isArray(value) && value.length === 0) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per lunghezza minima
 */
export function minLength(
  min: number,
  message?: string
): Validator {
  return (value: any): ValidationResult => {
    const defaultMessage = `Minimo ${min} caratteri richiesti`;

    if (value === null || value === undefined) {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const length = typeof value === 'string' ? value.length : String(value).length;

    if (length < min) {
      return { valid: false, error: message || defaultMessage };
    }

    return { valid: true };
  };
}

/**
 * Validatore per lunghezza massima
 */
export function maxLength(
  max: number,
  message?: string
): Validator {
  return (value: any): ValidationResult => {
    const defaultMessage = `Massimo ${max} caratteri consentiti`;

    if (value === null || value === undefined) {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const length = typeof value === 'string' ? value.length : String(value).length;

    if (length > max) {
      return { valid: false, error: message || defaultMessage };
    }

    return { valid: true };
  };
}

/**
 * Validatore per formato email
 */
export function email(message: string = 'Inserisci un indirizzo email valido'): Validator {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const stringValue = String(value);

    if (!emailRegex.test(stringValue)) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per formato telefono italiano
 */
export function phone(message: string = 'Inserisci un numero di telefono valido'): Validator {
  // Supporta formati italiani: +39, 0039, fissi e mobili
  const phoneRegex = /^(\+39|0039)?[\s]?([0-9]{2,4})[\s]?([0-9]{6,8})$/;

  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const stringValue = String(value).replace(/[\s\-()]/g, '');

    if (!phoneRegex.test(stringValue)) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per formato URL
 */
export function url(message: string = 'Inserisci un URL valido'): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const stringValue = String(value);

    try {
      new URL(stringValue);
      return { valid: true };
    } catch {
      return { valid: false, error: message };
    }
  };
}

/**
 * Validatore per pattern regex personalizzato
 */
export function pattern(
  regex: RegExp,
  message: string = 'Formato non valido'
): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const stringValue = String(value);

    if (!regex.test(stringValue)) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per valori numerici
 */
export function numeric(message: string = 'Inserisci un valore numerico valido'): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per formato data
 */
export function date(message: string = 'Inserisci una data valida'): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const dateValue = new Date(value);

    if (isNaN(dateValue.getTime())) {
      return { valid: false, error: message };
    }

    return { valid: true };
  };
}

/**
 * Validatore per valore minimo (numerico)
 */
export function min(
  minValue: number,
  message?: string
): Validator {
  return (value: any): ValidationResult => {
    const defaultMessage = `Il valore deve essere almeno ${minValue}`;

    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return { valid: true }; // Lascia a numeric() la validazione del formato
    }

    if (numValue < minValue) {
      return { valid: false, error: message || defaultMessage };
    }

    return { valid: true };
  };
}

/**
 * Validatore per valore massimo (numerico)
 */
export function max(
  maxValue: number,
  message?: string
): Validator {
  return (value: any): ValidationResult => {
    const defaultMessage = `Il valore deve essere al massimo ${maxValue}`;

    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Usa required() per obbligatorietà
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return { valid: true }; // Lascia a numeric() la validazione del formato
    }

    if (numValue > maxValue) {
      return { valid: false, error: message || defaultMessage };
    }

    return { valid: true };
  };
}

// ============================================================================
// VALIDATORI COMBINATI
// ============================================================================

/**
 * Compone più validatori in sequenza
 * Si ferma al primo errore
 */
export function compose(...validators: Validator[]): Validator {
  return (value: any): ValidationResult => {
    for (const validator of validators) {
      const result = validator(value);
      if (!result.valid) {
        return result;
      }
    }
    return { valid: true };
  };
}

/**
 * Rende un validatore opzionale (salta la validazione se il valore è vuoto)
 */
export function optional(validator: Validator): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { valid: true };
    }
    return validator(value);
  };
}

// ============================================================================
// HOOK REACT
// ============================================================================

/**
 * Opzioni per l'hook useFormValidation
 */
export interface UseFormValidationOptions {
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

/**
 * Risultato dell'hook useFormValidation
 */
export interface UseFormValidationResult {
  errors: ValidationErrors;
  touched: TouchedFields;
  isValid: boolean;
  validate: (values: FormValues) => boolean;
  validateField: (fieldName: string, value: any) => boolean;
  setTouched: (fieldName: string, isTouched?: boolean) => void;
  setFieldError: (fieldName: string, error: string | undefined) => void;
  reset: () => void;
}

/**
 * Hook per la validazione di form
 *
 * @example
 * ```tsx
 * const { errors, validate, isValid, touched, setTouched } = useFormValidation({
 *   email: [required(), email()],
 *   password: [required(), minLength(12)]
 * });
 *
 * const handleSubmit = (values: FormValues) => {
 *   if (validate(values)) {
 *     // Submit form
 *   }
 * };
 *
 * const handleBlur = (fieldName: string) => {
 *   setTouched(fieldName, true);
 * };
 * ```
 */
export function useFormValidation(
  schema: ValidationSchema,
  options: UseFormValidationOptions = {}
): UseFormValidationResult {
  const { validateOnChange = false, validateOnBlur = true } = options;

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouchedState] = useState<TouchedFields>({});

  /**
   * Valida un singolo campo
   */
  const validateField = useCallback((fieldName: string, value: any): boolean => {
    const validators = schema[fieldName];

    if (!validators || validators.length === 0) {
      return true;
    }

    // Esegui tutti i validatori in sequenza
    for (const validator of validators) {
      const result = validator(value);

      if (!result.valid) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: result.error
        }));
        return false;
      }
    }

    // Rimuovi l'errore se la validazione passa
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });

    return true;
  }, [schema]);

  /**
   * Valida tutti i campi del form
   */
  const validate = useCallback((values: FormValues): boolean => {
    const newErrors: ValidationErrors = {};
    let hasErrors = false;

    // Valida ogni campo nello schema
    for (const [fieldName, validators] of Object.entries(schema)) {
      const value = values[fieldName];

      // Esegui tutti i validatori per questo campo
      for (const validator of validators) {
        const result = validator(value);

        if (!result.valid) {
          newErrors[fieldName] = result.error;
          hasErrors = true;
          break; // Primo errore trovato, passa al campo successivo
        }
      }
    }

    setErrors(newErrors);

    // Marca tutti i campi come toccati
    const allTouched: TouchedFields = {};
    for (const fieldName of Object.keys(schema)) {
      allTouched[fieldName] = true;
    }
    setTouchedState(allTouched);

    return !hasErrors;
  }, [schema]);

  /**
   * Marca un campo come toccato
   */
  const setTouched = useCallback((fieldName: string, isTouched: boolean = true) => {
    setTouchedState(prev => ({
      ...prev,
      [fieldName]: isTouched
    }));
  }, []);

  /**
   * Imposta manualmente un errore per un campo
   */
  const setFieldError = useCallback((fieldName: string, error: string | undefined) => {
    setErrors(prev => {
      if (error === undefined) {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      }
      return {
        ...prev,
        [fieldName]: error
      };
    });
  }, []);

  /**
   * Reset dello stato di validazione
   */
  const reset = useCallback(() => {
    setErrors({});
    setTouchedState({});
  }, []);

  /**
   * Calcola se il form è valido
   */
  const isValid = Object.keys(errors).length === 0;

  return {
    errors,
    touched,
    isValid,
    validate,
    validateField,
    setTouched,
    setFieldError,
    reset
  };
}

/**
 * Utilità per ottenere il messaggio di errore di un campo
 * Restituisce l'errore solo se il campo è stato toccato
 */
export function getFieldError(
  fieldName: string,
  errors: ValidationErrors,
  touched: TouchedFields
): string | undefined {
  return touched[fieldName] ? errors[fieldName] : undefined;
}

/**
 * Utilità per verificare se un campo ha un errore
 */
export function hasFieldError(
  fieldName: string,
  errors: ValidationErrors,
  touched: TouchedFields
): boolean {
  return touched[fieldName] === true && errors[fieldName] !== undefined;
}
